import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { Sakura } from "../components/shrine/Sakura";
import { Miko } from "../components/miko/Miko";
import { setAmbient, useSound } from "../hooks/useSound";
import { useVibration } from "../hooks/useVibration";
import {
  MOOD,
  daysBetween,
  getTimeOfDay,
  pickHomeMessage,
  type TimeOfDay,
} from "../lib/mood";
import { getLastDraw, getProfile } from "../lib/storage";

const tabs = [
  { to: "/draw", icon: "🎴", key: "home.draw" },
  { to: "/pair", icon: "👥", key: "home.pair" },
  { to: "/collection", icon: "📜", key: "home.collection" },
  { to: "/progress", icon: "⛩", key: "home.progress" },
  { to: "/settings", icon: "⚙", key: "home.settings" },
] as const;

export function Home() {
  const { t, i18n } = useTranslation();
  const { unlock, play } = useSound();
  const { vibrate } = useVibration();
  const ambientStartedRef = useRef(false);
  const [audioOn, setAudioOn] = useState(false);

  const [tod, setTod] = useState<TimeOfDay>(() => getTimeOfDay());
  const [message, setMessage] = useState<string>("");
  const [voicedAt, setVoicedAt] = useState<number | null>(null);

  // 時刻に応じた更新 (5 分毎)
  useEffect(() => {
    const update = () => setTod(getTimeOfDay());
    update();
    const id = window.setInterval(update, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // セリフ動的生成
  useEffect(() => {
    const profile = getProfile();
    const last = getLastDraw();
    const days = daysBetween(last?.drawnAt);
    setMessage(
      pickHomeMessage({
        locale: i18n.language === "en" ? "en" : "ja",
        timeOfDay: tod,
        lastDraw: last,
        daysSinceLastDraw: days,
        // pendingPartnerName: 通知が届いていれば挿入 (Phase 2 で push 連携)
      }),
    );
    // profile が無くても表示は壊さない
    void profile;
  }, [tod, i18n.language]);

  const mood = MOOD[tod];

  // BGM ambient: ユーザータップまで開始しない (iOS の AudioContext 制約)
  function enableAudio() {
    if (ambientStartedRef.current) return;
    ambientStartedRef.current = true;
    unlock();
    setAmbient(mood.baseHz, mood.partialHz);
    setAudioOn(true);
  }

  // 時刻が変わったら ambient を切替
  useEffect(() => {
    if (!ambientStartedRef.current) return;
    setAmbient(mood.baseHz, mood.partialHz);
  }, [mood.baseHz, mood.partialHz]);

  // ページ離脱で停止
  useEffect(() => {
    return () => setAmbient(null);
  }, []);

  function tapMiko() {
    unlock();
    if (!ambientStartedRef.current) enableAudio();
    play("mikoVoice");
    vibrate(20);
    setVoicedAt(Date.now());
  }

  // 巫女の小ボイスエフェクト (光の輪)
  const voiceKey = useMemo(() => voicedAt ?? 0, [voicedAt]);

  return (
    <main
      className="relative min-h-dvh flex flex-col text-[var(--color-washi)] overflow-hidden"
      style={{ background: mood.background }}
    >
      <Sakura count={tod === "night" ? 10 : 14} />

      {/* 鳥居シルエット */}
      <motion.div
        aria-hidden
        className="absolute inset-x-0 top-[8%] flex justify-center pointer-events-none"
        animate={{ opacity: [0.18, 0.28, 0.18] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      >
        <span
          className="text-[10rem] leading-none"
          style={{
            color:
              tod === "night" ? "rgba(159,29,53,0.45)" : "rgba(159,29,53,0.55)",
            textShadow: `0 0 32px ${mood.glow}`,
            filter: "blur(0.5px)",
          }}
        >
          ⛩
        </span>
      </motion.div>

      <div className="relative flex-1 flex flex-col items-center justify-center px-6 pt-12 pb-32 z-10">
        {/* 巫女タップで小ボイス */}
        <motion.button
          onClick={tapMiko}
          whileTap={{ scale: 0.96 }}
          className="relative outline-none"
          aria-label={t("welcome.title")}
          animate={{ y: [0, -6, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        >
          {/* 光の輪 (タップ時のみ) */}
          {voicedAt && (
            <motion.div
              key={voiceKey}
              aria-hidden
              className="absolute inset-0 -z-10 rounded-full"
              initial={{ scale: 0.4, opacity: 0.6 }}
              animate={{ scale: 2.4, opacity: 0 }}
              transition={{ duration: 1.4, ease: "easeOut" }}
              style={{
                background: `radial-gradient(circle, ${mood.glow} 0%, transparent 60%)`,
              }}
            />
          )}
          <Miko
            size={220}
            className="drop-shadow-[0_8px_24px_rgba(159,29,53,0.45)]"
          />
        </motion.button>

        <motion.p
          key={message}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2 }}
          className="mt-6 text-base text-center max-w-xs leading-relaxed"
          style={{
            fontFamily: "var(--font-fude)",
            color: mood.ink,
            textShadow: "0 2px 8px rgba(0,0,0,0.6)",
          }}
        >
          {message}
        </motion.p>

        <p
          className="mt-4 text-[10px] tracking-[0.4em] opacity-60"
          style={{ color: "var(--color-gold-light)" }}
        >
          ✦ {i18n.language === "en" ? mood.label.en : mood.label.ja} ✦
        </p>

        {/* 音 ON/OFF — ユーザータップで AudioContext unlock */}
        {!audioOn && (
          <button
            onClick={enableAudio}
            className="mt-6 px-4 py-1.5 rounded-full text-[10px] tracking-widest"
            style={{
              background: "rgba(10,6,8,0.6)",
              border: "1px solid rgba(201,169,97,0.4)",
              color: "var(--color-gold-light)",
            }}
          >
            ♪{" "}
            {i18n.language === "en" ? "Enable shrine audio" : "境内の音を聴く"}
          </button>
        )}
      </div>

      <nav className="fixed bottom-0 inset-x-0 grid grid-cols-5 bg-[var(--color-night-deep)]/95 backdrop-blur-md border-t border-[var(--color-gold)]/20 pb-[env(safe-area-inset-bottom)] z-20">
        {tabs.map((tab) => (
          <Link
            key={tab.to}
            href={tab.to}
            className="py-3 flex flex-col items-center gap-1 text-[10px] opacity-80 hover:opacity-100 active:scale-95 transition"
          >
            <span className="text-2xl">{tab.icon}</span>
            <span>{t(tab.key)}</span>
          </Link>
        ))}
      </nav>
    </main>
  );
}
